import { chunk, groupBy, uniq } from 'lodash'
import { useEffect, useState } from 'react'
import type { Patient } from 'app/generated/graphql'
import { useContactStore, usePatientStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import type { WorkspaceFamilies } from 'app/types/related-contacts'
import { buildWorkspaceFamilies, guarantorKeys } from 'lib/relatedContacts'

const LEAD_LIMIT = 200
const BATCH = 100
const ACTIVE_LEADS_SEARCH = `leadStatus.keyword:("Needs Action" OR "In Progress")`

export function useWorkspaceFamilies(): WorkspaceFamilies {
  const contactStore = useContactStore()
  const patientStore = usePatientStore()
  const [state, setState] = useState<WorkspaceFamilies>({ loading: true, families: [], leadCount: 0 })

  useEffect(() => {
    let cancelled = false
    bg(async () => {
      const searchAll = async <T>(
        values: string[],
        run: (batch: string[]) => Promise<{ hits: T[]; error?: { message: string } }>,
      ) => {
        const results = await Promise.all(chunk(values, BATCH).map(run))
        const failed = results.find((r) => r.error)
        if (failed?.error) throw new Error(failed.error.message)
        return results.flatMap((r) => r.hits)
      }

      try {
        const leadResult = await contactStore.search({ search: ACTIVE_LEADS_SEARCH, pagination: { limit: LEAD_LIMIT } })
        if (leadResult.error) throw new Error(leadResult.error.message)
        const leads = leadResult.hits as ContactJson[]

        const patients = await searchAll<Patient>(uniq(leads.flatMap((c) => c.patientIds ?? [])), (ids) =>
          patientStore.search({
            search: `id:(${ids.map((v) => `"${v}"`).join(` OR `)})`,
            pagination: { limit: ids.length },
          }),
        )
        const keys = uniq(guarantorKeys(patients))
        const byLocation = groupBy(keys, (key) => key.split(`:`)[0])
        const guarantorPatients = (
          await Promise.all(
            Object.entries(byLocation).map(([locationId, locationKeys]) =>
              searchAll<Patient>(
                locationKeys.map((key) => key.split(`:`)[1]),
                (pmsIds) =>
                  patientStore.search({
                    search: `locationId:"${locationId}" AND pmsPatientId:(${pmsIds.map((v) => `"${v}"`).join(` OR `)})`,
                    pagination: { limit: pmsIds.length },
                  }),
              ),
            ),
          )
        ).flat()
        const guarantorContacts = await searchAll<ContactJson>(
          guarantorPatients.map((p) => p.id),
          (ids) =>
            contactStore.search({
              search: `patientIds:(${ids.map((v) => `"${v}"`).join(` OR `)})`,
              pagination: { limit: ids.length * 2 },
            }),
        )
        const phoneContacts = await searchAll<ContactJson>(
          uniq(leads.flatMap((c) => (c.cellPhone ? [c.cellPhone] : []))),
          (phones) =>
            contactStore.search({
              search: `cellPhone:(${phones.map((v) => `"${v}"`).join(` OR `)})`,
              pagination: { limit: 1000 },
            }),
        )

        const guarantorContactByPatientId = new Map(
          guarantorContacts.flatMap((c) => (c.patientIds ?? []).map((id) => [id, c] as const)),
        )
        const contactsByPhone = new Map(Object.entries(groupBy(phoneContacts, (c) => `${c.locationId}:${c.cellPhone}`)))
        const families = buildWorkspaceFamilies({
          leads,
          patientsById: new Map(patients.map((p) => [p.id, p])),
          guarantorPatients,
          guarantorContactByPatientId,
          contactsByPhone,
          now: new Date(),
        }).sort((a, b) => b.members.length - a.members.length)
        if (!cancelled) setState({ loading: false, families, leadCount: leads.length })
      } catch (error) {
        logError(`Related contacts prototype could not build families`, error)
        if (!cancelled) setState({ loading: false, families: [], leadCount: 0, error: (error as Error).message })
      }
    })
    return () => {
      cancelled = true
    }
  }, [contactStore, patientStore])

  return state
}
